import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Clock3 } from "lucide-react";
import { C, font } from "../theme";
import { Button } from "./Button";

const HOURS = Array.from({ length: 24 }, (_, value) => String(value).padStart(2, "0"));
const MINUTES = ["00", "15", "30", "45"];
const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];
const MONTHS = Array.from({ length: 12 }, (_, month) =>
  new Date(2020, month, 1).toLocaleDateString("pt-BR", { month: "long" }),
);

const triggerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
  boxSizing: "border-box",
  minHeight: 40,
  padding: "9px 10px",
  color: C.ink,
  background: C.white,
  border: `1px solid ${C.faint}`,
  borderRadius: 10,
  fontFamily: font,
  fontSize: 13,
  textAlign: "left",
};

const panelStyle: CSSProperties = {
  position: "absolute",
  zIndex: 70,
  top: "calc(100% + 6px)",
  right: 0,
  width: "min(360px, calc(100vw - 32px))",
  boxSizing: "border-box",
  padding: 14,
  background: C.white,
  border: `1px solid ${C.faint}`,
  borderRadius: 14,
  boxShadow: "0 18px 48px rgba(17,17,17,0.16)",
};

function parseTime(value: string): [string, string] {
  const [hour = "08", minute = "00"] = value.split(":");
  return [hour.padStart(2, "0"), minute.padStart(2, "0")];
}

function timeLabel(value: string): string {
  return value ? value.slice(0, 5) : "Selecionar horário";
}

function useDismiss(open: boolean, close: () => void, panelRef?: React.RefObject<HTMLDivElement | null>) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef?.current?.contains(target)) close();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, close]);
  return rootRef;
}

function OptionButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      style={{
        width: "100%",
        minWidth: 0,
        minHeight: 32,
        padding: "5px 7px",
        color: active ? C.ink : C.gray,
        background: active ? C.lime : C.bg,
        border: `1px solid ${active ? C.lime : C.faint}`,
        borderRadius: 8,
        cursor: "pointer",
        fontFamily: font,
        fontSize: 11.5,
        fontWeight: active ? 700 : 500,
      }}
    >
      {label.replace("Hora ", "").replace("Minutos ", "")}
    </button>
  );
}

function TimeGrid({ hour, minute, onHourChange, onMinuteChange }: { hour: string; minute: string; onHourChange: (value: string) => void; onMinuteChange: (value: string) => void }) {
  return (
    <>
      <div style={{ color: C.light, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, margin: "10px 0 6px", textTransform: "uppercase" }}>Hora</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8,minmax(0,1fr))", gap: 5, width: "100%", minWidth: 0 }}>
        {HOURS.map(value => <OptionButton key={value} label={`Hora ${value}`} active={hour === value} onClick={() => onHourChange(value)} />)}
      </div>
      <div style={{ color: C.light, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, margin: "12px 0 6px", textTransform: "uppercase" }}>Minutos</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
        {MINUTES.map(value => <OptionButton key={value} label={`Minutos ${value}`} active={minute === value} onClick={() => onMinuteChange(value)} />)}
      </div>
    </>
  );
}

interface TimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
}

export function TimePicker({ id, value, onChange, ariaLabel, disabled = false }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const [[hour, minute], setDraft] = useState(() => parseTime(value));
  const close = () => setOpen(false);
  const rootRef = useDismiss(open, close);

  const show = () => {
    setDraft(parseTime(value));
    setOpen(true);
  };

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0 }}>
      <button id={id} type="button" aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} disabled={disabled} onClick={show} style={{ ...triggerStyle, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1 }}>
        <span>{timeLabel(value)}</span><Clock3 size={16} color={C.light} aria-hidden="true" />
      </button>
      {open && (
        <div role="dialog" aria-label={`${ariaLabel}: seletor de horário`} style={{ ...panelStyle, width: "min(344px, calc(100vw - 32px))" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <b style={{ color: C.ink, fontSize: 13 }}>Escolha o horário</b>
            <span style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: "5px 9px", fontSize: 13, fontWeight: 700 }}>{hour}:{minute}</span>
          </div>
          <TimeGrid hour={hour} minute={minute} onHourChange={next => setDraft([next, minute])} onMinuteChange={next => setDraft([hour, next])} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={() => { onChange(`${hour}:${minute}`); close(); }}>Confirmar horário</Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ParsedDateTime {
  date: Date;
  hour: string;
  minute: string;
}

function parseDateTime(value: string): ParsedDateTime {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(value);
  if (!match) {
    const today = new Date();
    return { date: new Date(today.getFullYear(), today.getMonth(), today.getDate()), hour: "08", minute: "00" };
  }
  return {
    date: new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
    hour: match[4] ?? "08",
    minute: match[5] ?? "00",
  };
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

type DateTimePickerMode = "date" | "datetime";

function dateTimeLabel(value: string, mode: DateTimePickerMode): string {
  if (!value) return mode === "date" ? "Selecionar data" : "Selecionar data e horário";
  const parsed = parseDateTime(value);
  const dateLabel = parsed.date.toLocaleDateString("pt-BR");
  return mode === "date" ? dateLabel : `${dateLabel} às ${parsed.hour}:${parsed.minute}`;
}

interface DateTimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  placement?: "above" | "below";
  /** `date` esconde o seletor de horário e emite apenas `YYYY-MM-DD`. */
  mode?: DateTimePickerMode;
  minYear?: number;
  maxYear?: number;
}

export function DateTimePicker({
  id,
  value,
  onChange,
  ariaLabel,
  disabled = false,
  placement = "above",
  mode = "datetime",
  minYear = new Date().getFullYear() - 100,
  maxYear = new Date().getFullYear() + 20,
}: DateTimePickerProps) {
  const initial = parseDateTime(value);
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initial.date);
  const [cursor, setCursor] = useState(new Date(initial.date.getFullYear(), initial.date.getMonth(), 1));
  const [hour, setHour] = useState(initial.hour);
  const [minute, setMinute] = useState(initial.minute);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const close = () => setOpen(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const rootRef = useDismiss(open, close, panelRef);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => setTriggerRect(triggerRef.current?.getBoundingClientRect() ?? null);
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return [...Array.from({ length: firstDay }, () => null), ...Array.from({ length: daysInMonth }, (_, index) => new Date(cursor.getFullYear(), cursor.getMonth(), index + 1))];
  }, [cursor]);
  const calendarYears = useMemo(() => {
    const firstYear = Math.min(minYear, cursor.getFullYear());
    const lastYear = Math.max(maxYear, cursor.getFullYear());
    return Array.from({ length: lastYear - firstYear + 1 }, (_, index) => lastYear - index);
  }, [cursor, minYear, maxYear]);

  const show = () => {
    const next = parseDateTime(value);
    setSelectedDate(next.date);
    setCursor(new Date(next.date.getFullYear(), next.date.getMonth(), 1));
    setHour(next.hour);
    setMinute(next.minute);
    setOpen(true);
  };

  const confirm = () => {
    onChange(mode === "date" ? dateKey(selectedDate) : `${dateKey(selectedDate)}T${hour}:${minute}`);
    close();
  };

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 0 }}>
      <button ref={triggerRef} id={id} type="button" aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} disabled={disabled} onClick={show} style={{ ...triggerStyle, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1 }}>
        <span>{dateTimeLabel(value, mode)}</span><CalendarDays size={16} color={C.light} aria-hidden="true" />
      </button>
      {open && triggerRect && createPortal(
        <div ref={panelRef} role="dialog" aria-label={`${ariaLabel}: ${mode === "date" ? "calendário" : "calendário e horário"}`} style={{ ...panelStyle, position: "fixed", left: Math.max(12, Math.min(triggerRect.left, window.innerWidth - 372)), top: placement === "below" ? triggerRect.bottom + 6 : undefined, bottom: placement === "above" ? window.innerHeight - triggerRect.top + 6 : undefined, maxHeight: `${Math.max(120, placement === "below" ? window.innerHeight - triggerRect.bottom - 18 : triggerRect.top - 18)}px`, overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <button type="button" aria-label="Mês anterior" onClick={() => setCursor(current => new Date(current.getFullYear(), current.getMonth() - 1, 1))} style={{ border: 0, background: C.bg, borderRadius: 8, padding: 7, cursor: "pointer" }}><ChevronLeft size={16} /></button>
            <b style={{ color: C.ink, fontSize: 13, textTransform: "lowercase" }}>{cursor.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</b>
            <button type="button" aria-label="Próximo mês" onClick={() => setCursor(current => new Date(current.getFullYear(), current.getMonth() + 1, 1))} style={{ border: 0, background: C.bg, borderRadius: 8, padding: 7, cursor: "pointer" }}><ChevronRight size={16} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 96px", gap: 8, marginTop: 10 }}>
            <select
              aria-label="Mês do calendário"
              value={cursor.getMonth()}
              onChange={event => setCursor(current => new Date(current.getFullYear(), Number(event.target.value), 1))}
              style={{ fontFamily: font, fontSize: 12, color: C.ink, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 8, padding: "7px 8px", textTransform: "capitalize" }}
            >
              {MONTHS.map((month, index) => <option key={month} value={index}>{month}</option>)}
            </select>
            <select
              aria-label="Ano do calendário"
              value={cursor.getFullYear()}
              onChange={event => setCursor(current => new Date(Number(event.target.value), current.getMonth(), 1))}
              style={{ fontFamily: font, fontSize: 12, color: C.ink, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 8, padding: "7px 8px" }}
            >
              {calendarYears.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginTop: 10 }}>
            {WEEKDAYS.map((label, index) => <span key={`${label}-${index}`} style={{ color: C.light, fontSize: 10.5, fontWeight: 700, textAlign: "center", padding: 4 }}>{label}</span>)}
            {calendarDays.map((date, index) => date ? (
              <button
                type="button"
                key={dateKey(date)}
                aria-label={date.toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}
                aria-pressed={dateKey(date) === dateKey(selectedDate)}
                onClick={() => setSelectedDate(date)}
                style={{ minHeight: 34, color: C.ink, background: dateKey(date) === dateKey(selectedDate) ? C.lime : C.bg, border: `1px solid ${dateKey(date) === dateKey(selectedDate) ? C.lime : C.faint}`, borderRadius: 8, cursor: "pointer", fontFamily: font, fontSize: 11.5, fontWeight: dateKey(date) === dateKey(selectedDate) ? 700 : 500 }}
              >{date.getDate()}</button>
            ) : <span key={`empty-${index}`} />)}
          </div>
          {mode !== "date" && <TimeGrid hour={hour} minute={minute} onHourChange={setHour} onMinuteChange={setMinute} />}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={confirm}>{mode === "date" ? "Confirmar data" : "Confirmar data e horário"}</Button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
